import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Ban,
  Copy,
  Lock,
  Mail,
  Megaphone,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  Users,
} from "lucide-react";
import PolicyDeskRailHead from "../components/policy/PolicyDeskRailHead";
import PolicyDeskSiblingNav from "../components/policy/PolicyDeskSiblingNav";
import { PolicySectionAccordion } from "../components/policy/PolicySectionAccordion";
import {
  COMMUNITY_GUIDELINES_META,
  COMMUNITY_PROMISES,
  COMMUNITY_SECTIONS,
} from "../utils/communityGuidelinesContent";
import "../privacy-policy.css";

const sectionIcons = {
  respect: MessageCircle,
  prohibited: Ban,
  fair: ShieldCheck,
  privacy: Lock,
  spam: Megaphone,
  enforcement: AlertTriangle,
  changes: RefreshCw,
};

const Community = () => {
  const contentRef = useRef(null);

  const [openSections, setOpenSections] = useState(() => new Set(["respect"]));
  const [activeSection, setActiveSection] = useState("respect");
  const [copied, setCopied] = useState(false);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setActiveSection(id);
  };

  const jumpToSection = (id) => {
    setOpenSections((prev) => new Set(prev).add(id));
    setActiveSection(id);
    document.getElementById(`pp-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id?.startsWith("pp-")) {
          setActiveSection(visible.target.id.replace("pp-", ""));
        }
      },
      { root, rootMargin: "-20% 0px -55% 0px", threshold: [0.1, 0.35, 0.6] }
    );

    COMMUNITY_SECTIONS.forEach(({ id }) => {
      const node = document.getElementById(`pp-${id}`);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText("betbondofficial@gmail.com");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  return (
    <div className="pp-page">
      <div className="pp-desk">
        <div className="pp-workspace">
          <aside className="pp-rail" aria-label="Guidelines overview">
            <PolicyDeskRailHead meta={COMMUNITY_GUIDELINES_META} icon={<Users size={18} />} />

            <div className="pp-promises">
              {COMMUNITY_PROMISES.map((promise) => (
                <div className="pp-promise" key={promise.id}>
                  <strong>{promise.label}</strong>
                  <span>{promise.detail}</span>
                </div>
              ))}
            </div>

            <nav className="pp-index" aria-label="Sections">
              <p className="pp-index-label">On this page</p>
              <ul>
                {COMMUNITY_SECTIONS.map((section) => {
                  const Icon = sectionIcons[section.id] || Users;
                  return (
                    <li key={section.id}>
                      <button
                        type="button"
                        className={`pp-index-btn${activeSection === section.id ? " pp-index-btn-active" : ""}`}
                        onClick={() => jumpToSection(section.id)}
                      >
                        <span className="pp-index-icon" aria-hidden>
                          <Icon size={11} />
                        </span>
                        <span>{section.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <PolicyDeskSiblingNav currentId="community" />

            <div className="pp-rail-contact">
              <Mail size={13} aria-hidden />
              <div>
                <strong>Report a concern?</strong>
                <button type="button" className="pp-copy-email" onClick={copyEmail}>
                  betbondofficial@gmail.com
                  <Copy size={10} aria-hidden />
                </button>
                {copied ? <em>Copied</em> : null}
              </div>
            </div>
          </aside>

          <div className="pp-content" ref={contentRef}>
            <section className="pp-intro-card">
              <div className="pp-intro-top">
                <span className="pp-intro-badge" aria-hidden>
                  <Users size={12} />
                </span>
                <span>Overview</span>
              </div>
              <p className="pp-intro-text">{COMMUNITY_GUIDELINES_META.intro}</p>
              <p className="pp-intro-note">{COMMUNITY_GUIDELINES_META.platformNote}</p>
            </section>

            <div className="pp-sections">
              {COMMUNITY_SECTIONS.map((section) => (
                <PolicySectionAccordion
                  key={section.id}
                  section={section}
                  open={openSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                />
              ))}
            </div>

            <footer className="pp-contact-strip">
              <div>
                <p className="pp-contact-eyebrow">See something wrong?</p>
                <h2>Report through the app</h2>
                <p>
                  Use in-app reporting for content or behavior that breaks these guidelines. For
                  urgent safety issues, email us with details.
                </p>
              </div>
              <a className="pp-contact-btn" href="mailto:betbondofficial@gmail.com">
                <Mail size={14} aria-hidden />
                betbondofficial@gmail.com
              </a>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;
